import { PointMetadata, usePointCloudContext } from 'features/pointClouds'
import React from 'react'
import styled from 'styled-components'

interface PointCloudTooltipProps {
  position?: { x: number; y: number }
  pointMetadata?: PointMetadata | null
}

const TooltipContainer = styled.div`
  position: fixed;
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', monospace;
  pointer-events: none;
  z-index: 10000;
  white-space: nowrap;
  transform: translateX(-50%) translateY(-100%);
  transition: opacity 0.15s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.85);
  }
`

const ColorValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ColorSwatch = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  background-color: ${({ $color }) => $color};
  border: 1px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
`

const Label = styled.span`
  opacity: 0.8;
  margin-right: 4px;
`

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(n * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function formatTooltip(pointMetadata: PointMetadata): React.ReactNode {
  if (pointMetadata.rgb) {
    const { r, g, b } = pointMetadata.rgb
    const hexColor = rgbToHex(r, g, b)
    return (
      <ColorValue>
        <ColorSwatch $color={hexColor} />
        {pointMetadata.thickness?.toPrecision(4)}&quot;
      </ColorValue>
    )
  }

  return (
    <ColorValue>
      <Label>No color data</Label>
    </ColorValue>
  )
}

export const PointCloudTooltip: React.FC<PointCloudTooltipProps> = ({
  position,
  pointMetadata,
}) => {
  const { enableTooltips } = usePointCloudContext()

  if (
    !pointMetadata ||
    !position ||
    !enableTooltips ||
    !pointMetadata.thickness
  ) {
    return null
  }

  return (
    <TooltipContainer
      style={{
        left: position.x,
        top: position.y - 10,
      }}>
      {formatTooltip(pointMetadata)}
    </TooltipContainer>
  )
}
